import React, { createContext, useContext, ReactNode } from 'react';
import { useHttpClientCore } from '@/shared';
import { ISection2Repository } from '../../application/ports/outbound/ISection2Repository';
import { ApiSection2Repository } from '../repositories/ApiSection2Repository';
import UserUseCase from '../../application/useCase/UserUseCase';
import { ApiUserRepository } from '../repositories/ApiUserRepository';
import { ApiAmendmentRepository } from '@/features/section-1/infra/repositories/ApiAmendmentRepository';
import { IAmendmentRepository } from '@/features/section-1/application/ports/outbound/IAmendmentRepository';
import { useAuth } from '@/shared/auth';
import { ApiRemoteI9Repository } from '@/features/remote_i9/infra/repositories/ApiRemoteI9Repository';
import { IRemoteI9Repository } from '@/features/remote_i9/application/ports/outbound/IRemoteI9Repository';
import { IRemoteI9AmendmentRepository } from '@/features/remote_i9/application/ports/outbound/IRemoteI9AmendmentRepository';
import ApiRemoteI9AmendmentRepository from '@/features/remote_i9/infra/repositories/ApiRemoteI9AmendmentRepository';
import { ISectionOneRepository } from '@/features/section-1/application/ports/outbound/ISectionOneRepository';
import { ApiSectionOneDataRepository } from '@/features/section-1/infra/repositories/ApiSectionOneDataRepository';

/**
 * Section 2 Dependency Injection Context
 * Provides repository instances to components
 */
interface Section2DIContextType {
  section2Repository: ISection2Repository | IRemoteI9Repository;
  // The remote completer flow: drives the repository choice below, and lets consumers know the
  // form id lives in the auth token (not on the FE) so token-based fetches are safe without it.
  isRemoteI9User: boolean;
  userUseCase: UserUseCase;
  amendmentRepository: IAmendmentRepository | IRemoteI9AmendmentRepository;
  sectionOneRepository: ISectionOneRepository;
}

const Section2DIContext = createContext<Section2DIContextType | undefined>(undefined);

interface Section2DIProviderProps {
  children: ReactNode;
}

export const Section2DIProvider: React.FC<Section2DIProviderProps> = ({ children }) => {
  const httpClient = useHttpClientCore();
  const { isRemoteI9User } = useAuth();
  // Initialize repositories and use cases
  const section2Repository = isRemoteI9User ? new ApiRemoteI9Repository(httpClient) : new ApiSection2Repository(httpClient);
  const userRepository = new ApiUserRepository(httpClient);
  const userUseCase = new UserUseCase(userRepository);
  const amendmentRepository = isRemoteI9User ? new ApiRemoteI9AmendmentRepository(httpClient) : new ApiAmendmentRepository(httpClient);
  const sectionOneRepository = new ApiSectionOneDataRepository(httpClient);

  const contextValue: Section2DIContextType = {
    section2Repository,
    isRemoteI9User,
    userUseCase,
    amendmentRepository,
    sectionOneRepository,
  };

  return <Section2DIContext.Provider value={contextValue}>{children}</Section2DIContext.Provider>;
};

/**
 * Hook to access Section 2 DI context
 */
export const useSection2DIContext = (): Section2DIContextType => {
  const context = useContext(Section2DIContext);
  if (!context) {
    throw new Error('useSection2DIContext must be used within Section2DIProvider');
  }
  return context;
};
